import React from "react";
import LegalPageLayout from "@user/components/LegalPageLayout";
import { SUPPORT_EMAIL } from "@/config/support";

/**
 * Refund & Cancellation Policy — Cashfree specifically checks for this page
 * before approving a merchant. The policy must be clear on eligibility, the
 * refund window, and the timeframe within which refunds are processed.
 */
export default function RefundPolicy() {
  return (
    <LegalPageLayout title="Refund & Cancellation Policy" lastUpdated="7 February 2026">
      <p>
        This Refund &amp; Cancellation Policy explains when and how you can
        cancel a consultation booked through Advoset and claim a refund. It
        forms part of our <a href="/terms">Terms &amp; Conditions</a>. This
        policy applies to all payments processed on{" "}
        <strong>advoset.in</strong> and its sub-applications.
      </p>

      <h2>1. Consultation cancellations</h2>
      <h3>1.1 Cancellation by the client</h3>
      <ul>
        <li>
          <strong>More than 6 hours before the scheduled start</strong> — full
          refund of the consultation fee, net of applicable payment-gateway
          charges.
        </li>
        <li>
          <strong>Between 1 and 6 hours before the scheduled start</strong> —
          50% refund. The remaining 50% covers the lawyer&apos;s reserved time.
        </li>
        <li>
          <strong>Less than 1 hour before the scheduled start, or after the
          consultation begins</strong> — non-refundable.
        </li>
      </ul>
      <h3>1.2 Cancellation by the lawyer</h3>
      <p>
        If the lawyer cancels or fails to join the consultation, you are
        entitled to either a <strong>100% refund</strong> or the option to
        reschedule with the same or a different lawyer at no extra cost.
      </p>
      <h3>1.3 Cancellation by Advoset</h3>
      <p>
        Advoset may cancel a consultation if we detect fraud, misuse, or a
        material breach of our <a href="/terms">Terms &amp; Conditions</a>. In
        genuine cancellations by Advoset, you receive a full refund.
      </p>

      <h2>2. Refund eligibility for completed consultations</h2>
      <p>
        Once a consultation is completed, the fee is generally non-refundable.
        However, you can request a refund within <strong>48 hours</strong> of
        completion in the following cases:
      </p>
      <ul>
        <li>
          The lawyer joined the consultation but was unable to provide the
          service due to a technical issue on our platform.
        </li>
        <li>
          The lawyer did not have the practice-area competence advertised on
          their Advoset profile.
        </li>
        <li>
          The consultation was materially shorter than the duration you paid
          for, and the shortfall was caused by the lawyer.
        </li>
      </ul>
      <p>
        Refund requests based on the quality or outcome of legal advice are not
        entertained; those are matters between you and the lawyer and, if
        necessary, with the relevant State Bar Council.
      </p>

      <h2>3. How to request a refund</h2>
      <ol>
        <li>
          Write to <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> from
          your registered email address within the eligibility window.
        </li>
        <li>
          Include your booking ID (visible in your dashboard), consultation
          date, and the reason for the refund.
        </li>
        <li>
          Our team will acknowledge your request within one working day and
          share a decision within five working days.
        </li>
      </ol>

      <h2>4. Refund timelines</h2>
      <p>
        Approved refunds are initiated to the original payment instrument
        within <strong>2 working days</strong> of approval. The money typically
        reflects in your account within <strong>5 to 7 working days</strong>,
        subject to your bank or card issuer&apos;s processing time.
      </p>

      <h2>5. Refund method</h2>
      <ul>
        <li>
          Refunds are processed only to the <strong>original payment method</strong>{" "}
          (UPI, card, netbanking, or wallet). We do not issue refunds in cash
          or to a different account.
        </li>
        <li>
          Payment-gateway convenience fees, if any, may not be refundable and
          will be shown clearly at checkout.
        </li>
      </ul>

      <h2>6. Non-refundable items</h2>
      <ul>
        <li>Platform subscription fees, if any, once the billing period has begun.</li>
        <li>Fees paid directly to a lawyer outside of Advoset&apos;s platform.</li>
        <li>Fees for services already delivered in full.</li>
      </ul>

      <h2>7. Chargebacks</h2>
      <p>
        Please contact us before raising a chargeback with your bank &mdash;
        most disputes can be resolved directly and faster. Chargebacks raised
        for services delivered in good faith may be contested with evidence of
        service delivery.
      </p>

      <h2>8. Contact</h2>
      <p>
        For questions about this policy, write to{" "}
        <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> or use our{" "}
        <a href="/contact">Contact Us</a> page.
      </p>
    </LegalPageLayout>
  );
}
